import { writable, type Readable } from 'svelte/store';
import { setConsentCookie, currentConsentCookieState, ConsentCookieState } from './cookies';

export enum PreferenceState {
  Yes = 'Yes',
  No = 'No',
  Unknown = 'Unknown'
}

/**
 * Provides users geo specific context to determine if analytics should
 * be disabled and if consent should be requested. Does not take into
 * account the user's preference.
 */
interface UserContextState {
  isDisabled: boolean;
  shouldRequestConsent: boolean;
}

/**
 * Represents the current state of the analytics store. Takes into account
 * the user's preference.
 */
export interface AnalyticsStoreState {
  isDisabled: PreferenceState;
  shouldRequestConsent: PreferenceState;
}

export interface AnalyticsStore extends Readable<AnalyticsStoreState> {
  init: (userContextState: UserContextState) => void;
  acceptAnalytics: () => void;
  declineAnalytics: () => void;
}

const isConsentRequired = (userContextState: UserContextState): PreferenceState => {
  if (!userContextState.shouldRequestConsent) {
    return PreferenceState.No;
  }

  const state = currentConsentCookieState();

  if (state === ConsentCookieState.Empty || state === ConsentCookieState.Expired) {
    return PreferenceState.Yes;
  }

  return PreferenceState.No;
};

export function shouldDisableAnalytics(userContextState: UserContextState): PreferenceState {
  if (userContextState.isDisabled) {
    return PreferenceState.Yes;
  }

  if (!userContextState.shouldRequestConsent) {
    return PreferenceState.No;
  }

  switch (currentConsentCookieState()) {
    case ConsentCookieState.Allowed:
      return PreferenceState.No;
    case ConsentCookieState.Declined:
      return PreferenceState.Yes;
    default:
      return PreferenceState.Unknown;
  }
}

export const createAnalyticsStore = (): AnalyticsStore => {
  const { subscribe, set } = writable<AnalyticsStoreState>({
    isDisabled: PreferenceState.Unknown,
    shouldRequestConsent: PreferenceState.Unknown
  });

  const init = (userContextState: UserContextState) => {
    set({
      isDisabled: shouldDisableAnalytics(userContextState),
      shouldRequestConsent: isConsentRequired(userContextState)
    });
  };

  const acceptAnalytics = () => {
    setConsentCookie(true);
    set({ isDisabled: PreferenceState.No, shouldRequestConsent: PreferenceState.No });
  };

  const declineAnalytics = () => {
    setConsentCookie(false);
    set({ isDisabled: PreferenceState.Yes, shouldRequestConsent: PreferenceState.No });
  };

  return {
    subscribe,
    init,
    acceptAnalytics,
    declineAnalytics
  };
};

export const analyticsStore = createAnalyticsStore();
